import { PII_MASK_CLASS } from "@/lib/analytics";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { ReorderButton } from "@/components/reorder";
import { REORDER_BUTTON_TONE_CLASS } from "@/components/reorder/ReorderButton";
import { TrackLineButton } from "@/components/stock-tracking/TrackLineButton";
import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardHeading,
  CardTitle
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useCustomerOrder,
  useCustomerOrders,
  useCustomerOrderStats,
  useCustomerOrderSuppliers,
  type CustomerOrder,
  type CustomerOrdersQueryParams
} from "@/queries/orders";
import {
  getOrderDisplayTotal,
  isCancelled
} from "@/queries/orders/order-insights";
import { convertToLocale } from "@/utils/money";
import type { HttpTypes } from "@medusajs/types";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { createRoute, useNavigate, useSearch } from "@tanstack/react-router";
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  DollarSign,
  HelpCircle,
  Mail,
  MapPin,
  MessageCircle,
  Package,
  Phone,
  RefreshCw,
  Search,
  ShoppingCart,
  Store
} from "lucide-react";
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { z } from "zod";
import { layoutRoute } from "../$layoutRoute";
import {
  FULFILLMENT_BUCKETS,
  FULFILLMENT_BUCKET_IDS,
  formatOrderDate,
  formatOrderNumber,
  getFulfillmentBucket,
  getFulfillmentStatusBadge,
  getPaymentStatusBadge,
  type FulfillmentBucketId
} from "./order-history-utils";

const PAGE_SIZE = 10;

/**
 * Status tabs. These filter server-side by the computed `fulfillment_status`
 * (via the endpoint's `fulfillment_status` param - salonstocker/api#130), so
 * the tab buckets line up with the per-row fulfillment badges. Each non-"All"
 * tab maps to a {@link FULFILLMENT_BUCKETS} entry.
 */
const STATUS_TABS: { id: string; label: string }[] = [
  { id: "all", label: "All" },
  ...FULFILLMENT_BUCKETS.map((bucket) => ({
    id: bucket.id,
    label: bucket.label
  }))
];

type SortKey = "date-desc" | "date-asc" | "total-desc" | "total-asc";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "date-desc", label: "Date (Newest first)" },
  { value: "date-asc", label: "Date (Oldest first)" },
  { value: "total-desc", label: "Total (High to low)" },
  { value: "total-asc", label: "Total (Low to high)" }
];

const searchSchema = z.object({
  page: z.number().int().min(1).optional(),
  /** Fulfillment-status tab bucket (drives the server-side filter). */
  fulfillment: z.enum(FULFILLMENT_BUCKET_IDS).optional(),
  /** Supplier (seller) id filter. */
  supplier: z.string().optional(),
  /** Created-at range, `YYYY-MM-DD` (from a native date input). */
  from: z.string().optional(),
  to: z.string().optional(),
  /** Order to open expanded on arrival (from the home page Recent Orders). */
  order: z.string().optional()
});

export const ordersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "orders",
  component: OrdersPage,
  validateSearch: searchSchema
});

/**
 * Order history browser (F1), styled after the pla-pla-store "Order Management"
 * page. Full-width page (sits in `MainLayout`) with header stat cards, a status-
 * tabbed Order History card with supplier/date/sort/search controls, and
 * expandable order rows that lazy-load each order's items, totals and shipping
 * address. Backed by the dedicated `/store/order-history` endpoint.
 *
 * Server-side filters (status tab, supplier, date range) drive a refetch;
 * sort and search operate on the currently loaded page.
 */
function OrdersPage() {
  const {
    page = 1,
    fulfillment,
    supplier,
    from,
    to,
    order: focusOrderId
  } = useSearch({ from: ordersRoute.id });
  const navigate = useNavigate();

  const [sortBy, setSortBy] = useState<SortKey>("date-desc");
  const [searchQuery, setSearchQuery] = useState("");

  const params = useMemo<CustomerOrdersQueryParams>(
    () => ({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      seller_id: supplier,
      fulfillmentStatus: getFulfillmentBucket(fulfillment)?.states,
      createdAtFrom: from ? new Date(from).toISOString() : undefined,
      createdAtTo: to
        ? new Date(`${to}T23:59:59.999Z`).toISOString()
        : undefined
    }),
    [page, fulfillment, supplier, from, to]
  );

  const { data, isLoading, error } = useCustomerOrders(params);
  useReportLoadFailure(error, "orders page");
  const { data: suppliers = [] } = useCustomerOrderSuppliers();
  const { data: stats, isLoading: statsLoading } = useCustomerOrderStats();

  const orders = data?.orders ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const activeTabId = fulfillment ?? "all";
  const hasServerFilters = Boolean(supplier || from || to);

  // Sort + search operate client-side on the currently loaded page.
  const displayedOrders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = query
      ? orders.filter((order) => {
          const number = formatOrderNumber(order).toLowerCase();
          const seller = order.seller?.name?.toLowerCase() ?? "";
          return number.includes(query) || seller.includes(query);
        })
      : orders;

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "date-asc":
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        case "total-desc":
          return getOrderDisplayTotal(b) - getOrderDisplayTotal(a);
        case "total-asc":
          return getOrderDisplayTotal(a) - getOrderDisplayTotal(b);
        case "date-desc":
        default:
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
      }
    });
  }, [orders, searchQuery, sortBy]);

  const totalSpent =
    stats &&
    convertToLocale({
      amount: stats.totalSpent,
      currency_code: stats.currencyCode
    });

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
          Order Management
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Track and manage all your salon supply orders
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Orders"
          value={stats?.active ?? 0}
          hint="Currently in progress"
          Icon={Clock}
          loading={statsLoading}
        />
        <StatCard
          title="Completed"
          value={stats?.completed ?? 0}
          hint="Successfully delivered"
          Icon={CheckCircle}
          loading={statsLoading}
        />
        <StatCard
          title="Total Orders"
          value={stats?.total ?? count}
          hint="All time orders"
          Icon={ShoppingCart}
          loading={statsLoading}
        />
        <StatCard
          title="Total Spent"
          value={totalSpent ?? "-"}
          hint={stats?.approximate ? "Recent orders" : "Across all orders"}
          Icon={DollarSign}
          loading={statsLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {/* Main column */}
        <div className="space-y-4 lg:col-span-3">
          <Card>
            <CardHeader className="py-4">
              <CardHeading>
                <CardTitle>Order History</CardTitle>
                <CardDescription>
                  View past orders, track delivery status and reorder your
                  essentials to keep your salon stocked.
                </CardDescription>
              </CardHeading>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Fulfillment-status tabs (server-side filter) */}
              <Tabs
                value={activeTabId}
                onValueChange={(value) =>
                  navigate({
                    to: "/orders",
                    search: (prev) => ({
                      ...prev,
                      fulfillment:
                        value === "all"
                          ? undefined
                          : (value as FulfillmentBucketId),
                      page: undefined
                    })
                  })
                }
              >
                <TabsList className="grid h-auto w-full grid-cols-3 gap-1 sm:h-10 sm:grid-cols-6">
                  {STATUS_TABS.map((tab) => (
                    <TabsTrigger key={tab.id} value={tab.id}>
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              {/* Filters */}
              <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Supplier">
                  <select
                    aria-label="Filter by supplier"
                    value={supplier ?? ""}
                    onChange={(e) => {
                      const value = e.target.value || undefined;
                      navigate({
                        to: "/orders",
                        search: (prev) => ({
                          ...prev,
                          supplier: value,
                          page: undefined
                        })
                      });
                    }}
                    className={selectClassName}
                  >
                    <option value="">All suppliers</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="From">
                  <input
                    type="date"
                    aria-label="Orders from date"
                    value={from ?? ""}
                    max={to ?? undefined}
                    onChange={(e) => {
                      const value = e.target.value || undefined;
                      navigate({
                        to: "/orders",
                        search: (prev) => ({
                          ...prev,
                          from: value,
                          page: undefined
                        })
                      });
                    }}
                    className={selectClassName}
                  />
                </Field>

                <Field label="To">
                  <input
                    type="date"
                    aria-label="Orders to date"
                    value={to ?? ""}
                    min={from ?? undefined}
                    onChange={(e) => {
                      const value = e.target.value || undefined;
                      navigate({
                        to: "/orders",
                        search: (prev) => ({
                          ...prev,
                          to: value,
                          page: undefined
                        })
                      });
                    }}
                    className={selectClassName}
                  />
                </Field>

                <Field label="Sort by">
                  <select
                    aria-label="Sort orders"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortKey)}
                    className={selectClassName}
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* Search + clear */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search
                    className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    aria-hidden="true"
                  />
                  <Input
                    placeholder="Search this page by order number or supplier..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    aria-label="Search orders"
                  />
                </div>
                {hasServerFilters && (
                  <Button
                    variant="ghost"
                    className="text-purple-600 hover:text-purple-700 dark:text-purple-400"
                    onClick={() =>
                      navigate({
                        to: "/orders",
                        search: (prev) => ({
                          ...prev,
                          supplier: undefined,
                          from: undefined,
                          to: undefined,
                          page: undefined
                        })
                      })
                    }
                  >
                    Clear filters
                  </Button>
                )}
              </div>

              {/* Orders list */}
              <section aria-label="Order history" className="pt-2">
                {isLoading && <OrdersLoadingSkeleton />}

                {error && !isLoading && (
                  <div
                    className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950"
                    role="alert"
                  >
                    <p className="text-lg font-medium text-red-600 dark:text-red-400">
                      Failed to load orders
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      We couldn&apos;t load your orders. Please try again later.
                    </p>
                  </div>
                )}

                {!isLoading && !error && orders.length === 0 && (
                  <OrdersEmptyState />
                )}

                {!isLoading &&
                  !error &&
                  orders.length > 0 &&
                  displayedOrders.length === 0 && (
                    <div
                      className="flex min-h-[160px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 text-center dark:border-gray-700"
                      role="status"
                    >
                      <Package
                        className="mb-3 h-10 w-10 text-gray-400"
                        aria-hidden="true"
                      />
                      <p className="text-base font-medium text-gray-900 dark:text-white">
                        No orders match your search
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Try a different order number or supplier.
                      </p>
                    </div>
                  )}

                {!isLoading && !error && displayedOrders.length > 0 && (
                  <>
                    <ul
                      className="space-y-3"
                      role="list"
                      aria-label="Order history"
                    >
                      {displayedOrders.map((order) => (
                        <li key={order.id}>
                          <OrderRow
                            order={order}
                            focused={order.id === focusOrderId}
                          />
                        </li>
                      ))}
                    </ul>

                    <Pagination
                      page={page}
                      totalPages={totalPages}
                      onChange={(next) =>
                        navigate({
                          to: "/orders",
                          search: (prev) => ({
                            ...prev,
                            page: next === 1 ? undefined : next
                          })
                        })
                      }
                    />
                  </>
                )}
              </section>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 lg:col-span-1">
          <HelpCard />
        </div>
      </div>
    </div>
  );
}

// ---- Stat card ----

const StatCard = memo(function StatCard({
  title,
  value,
  hint,
  Icon,
  loading
}: {
  title: string;
  value: string | number;
  hint: string;
  Icon: typeof Clock;
  loading?: boolean;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-sm font-medium">
          {title}
        </span>
        <Icon className="text-muted-foreground h-4 w-4" aria-hidden="true" />
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-8 w-20" />
      ) : (
        <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
          {value}
        </div>
      )}
      <p className="text-muted-foreground mt-1 text-xs">{hint}</p>
    </Card>
  );
});

// ---- Filter field wrapper ----

const selectClassName =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-muted-foreground mb-1 block text-xs font-medium">
        {label}
      </span>
      {children}
    </label>
  );
}

// ---- Order row (expandable) ----

/** Shared pill shape for the order status badges. */
const BADGE_PILL_CLASS =
  "max-w-full rounded-full px-2 py-0.5 text-xs font-medium";

const OrderRow = memo(function OrderRow({
  order,
  focused = false
}: {
  order: CustomerOrder;
  /** Deep-linked here from elsewhere: open it and bring it into view. */
  focused?: boolean;
}) {
  const [expanded, setExpanded] = useState(focused);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focused) return;
    setExpanded(true);
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focused]);
  const fulfillmentBadge = getFulfillmentStatusBadge(order.fulfillment_status);
  const paymentBadge = getPaymentStatusBadge(order.payment_status, {
    cancelled: isCancelled(order)
  });
  const supplierName = order.seller?.name;
  const productCount = order.items?.length ?? 0;
  const itemCount =
    order.items?.reduce((sum, item) => sum + (item.quantity ?? 0), 0) ?? 0;

  return (
    <div
      ref={cardRef}
      className={cn(
        "border-border bg-card scroll-mt-6 overflow-hidden rounded-lg border",
        focused && "ring-2 ring-purple-600 ring-offset-2 dark:ring-purple-400"
      )}
    >
      {/* Header: a column below sm (summary button, then a full-width Reorder
          row) so neither squeezes the other, and one row from sm up (summary
          button, then Reorder at the right). */}
      <div className="flex flex-col sm:flex-row sm:items-stretch">
        {/* Summary button: below sm a two-column grid (title and price/chevron
            on the first row, supplier and date full width below, the text
            block using `contents` so its lines are grid items); from sm up a
            flex row (icon, text block, price/chevron). */}
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className={cn(
            "grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 p-4 text-left transition-colors sm:items-center",
            "sm:flex sm:flex-1 sm:gap-4",
            "hover:bg-purple-50/40 dark:hover:bg-purple-900/10",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-600"
          )}
        >
          <div className="hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 sm:flex dark:bg-purple-900/40">
            <Package
              className="h-5 w-5 text-purple-600 dark:text-purple-400"
              aria-hidden="true"
            />
          </div>

          <div className="contents sm:block sm:min-w-0 sm:flex-1">
            <div className="col-start-1 row-start-1 flex min-w-0 flex-wrap items-center gap-2">
              <span className="whitespace-nowrap font-semibold text-gray-900 dark:text-white">
                {formatOrderNumber(order)}
              </span>
              <span
                className={cn(BADGE_PILL_CLASS, fulfillmentBadge.className)}
              >
                {fulfillmentBadge.label}
              </span>
              {paymentBadge && (
                <span className={cn(BADGE_PILL_CLASS, paymentBadge.className)}>
                  {paymentBadge.label}
                </span>
              )}
            </div>
            {supplierName && (
              <p className="col-span-2 mt-0.5 flex min-w-0 items-start gap-1 text-sm font-medium text-gray-700 sm:items-center dark:text-gray-300">
                {/* Below sm the icon is centred on the first line (h-5 is one
                    text-sm line) so a wrapped long name hangs beside it; from
                    sm up it is centred on the whole block. */}
                <span className="flex h-5 flex-shrink-0 items-center sm:h-auto">
                  <Store
                    className="h-3.5 w-3.5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
                <span className="min-w-0 break-words">{supplierName}</span>
              </p>
            )}
            <p className="col-span-2 mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {formatOrderDate(order.created_at)} &middot; {productCount}{" "}
              {productCount === 1 ? "product" : "products"} &middot; {itemCount}{" "}
              {itemCount === 1 ? "item" : "items"}
            </p>
          </div>

          <span className="col-start-2 row-start-1 flex flex-shrink-0 items-center gap-2 sm:gap-4">
            <span className="whitespace-nowrap font-semibold text-gray-900 dark:text-white">
              {convertToLocale({
                amount: getOrderDisplayTotal(order),
                currency_code: order.currency_code
              })}
            </span>
            {expanded ? (
              <ChevronDown
                className="h-5 w-5 flex-shrink-0 text-gray-400"
                aria-hidden="true"
              />
            ) : (
              <ChevronRight
                className="h-5 w-5 flex-shrink-0 text-gray-400"
                aria-hidden="true"
              />
            )}
          </span>
        </button>
        <div className="flex flex-shrink-0 items-center px-4 pb-4 sm:p-0 sm:pr-4">
          {/* Same purple circled control as the home Recent Orders card, with
              the label kept beside the icon. */}
          <ReorderButton
            orderId={order.id}
            seller={order.seller}
            currencyCode={order.currency_code}
            source="orders_page"
            variant="ghost"
            size="sm"
            label="Reorder"
            icon={RefreshCw}
            className={cn(
              "h-11 w-full flex-shrink-0 whitespace-nowrap px-3 sm:h-8 sm:w-auto",
              REORDER_BUTTON_TONE_CLASS
            )}
          />
        </div>
      </div>

      {expanded && <ExpandedOrderDetail orderId={order.id} />}
    </div>
  );
});

// ---- Expanded order detail (lazy) ----

const ExpandedOrderDetail = memo(function ExpandedOrderDetail({
  orderId
}: {
  orderId: string;
}) {
  const { data: order, isLoading, error } = useCustomerOrder(orderId);
  useReportLoadFailure(error, "order details");

  return (
    <div className="border-border bg-muted/40 border-t p-4">
      {isLoading && <ExpandedDetailSkeleton />}

      {error && !isLoading && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          We couldn&apos;t load this order. Please try again.
        </p>
      )}

      {!isLoading && !error && order && <OrderDetailBody order={order} />}

      {!isLoading && !error && !order && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Order details are unavailable.
        </p>
      )}
    </div>
  );
});

function OrderDetailBody({ order }: { order: CustomerOrder }) {
  const currency = order.currency_code;
  const fmt = (amount: number) =>
    convertToLocale({ amount, currency_code: currency });

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {/* Items + summary */}
      <div className="space-y-4 lg:col-span-2">
        <div>
          <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
            <Package
              className="h-4 w-4 text-purple-600 dark:text-purple-400"
              aria-hidden="true"
            />
            Order items
          </h4>
          {/* What a salon has bought before, at what price - masked out of
              session replays (ADR-0006). */}
          <div
            className={`divide-border border-border bg-card divide-y rounded-lg border ${PII_MASK_CLASS}`}
          >
            {order.items?.map((item) => (
              <OrderLineItem
                key={item.id}
                item={item}
                currencyCode={currency}
              />
            ))}
          </div>
        </div>

        <div className="border-border bg-card rounded-lg border p-4">
          <h5 className="mb-3 text-sm font-semibold text-gray-800 dark:text-gray-200">
            Order summary
          </h5>
          <dl className="space-y-2 text-sm">
            <SummaryRow
              label="Subtotal"
              value={fmt(order.item_subtotal ?? 0)}
            />
            <SummaryRow
              label="Shipping"
              value={
                (order.shipping_total ?? 0) === 0
                  ? "Free"
                  : fmt(order.shipping_total ?? 0)
              }
            />
            {(order.discount_total ?? 0) > 0 && (
              <SummaryRow
                label="Discount"
                value={`-${fmt(order.discount_total ?? 0)}`}
                valueClassName="text-green-600 dark:text-green-400"
              />
            )}
            {(order.tax_total ?? 0) > 0 && (
              <SummaryRow label="Tax" value={fmt(order.tax_total ?? 0)} />
            )}
            <div className="border-border border-t pt-2">
              <div className="flex items-center justify-between">
                <dt className="text-base font-semibold text-gray-900 dark:text-white">
                  Total
                </dt>
                <dd className="text-base font-bold text-gray-900 dark:text-white">
                  {fmt(getOrderDisplayTotal(order))}
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </div>

      {/* Shipping address */}
      <div className="lg:col-span-1">
        {order.shipping_address ? (
          <div>
            {/* Heading sits above the box, matching "Order items", so the two
                boxes start on the same line. */}
            <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
              <MapPin
                className="h-4 w-4 text-purple-600 dark:text-purple-400"
                aria-hidden="true"
              />
              Shipping address
            </h4>
            <div
              className={`border-border bg-card rounded-lg border p-4 text-sm text-gray-600 dark:text-gray-400 ${PII_MASK_CLASS}`}
            >
              <p className="font-medium text-gray-900 dark:text-white">
                {order.shipping_address.first_name}{" "}
                {order.shipping_address.last_name}
              </p>
              {order.shipping_address.company && (
                <p>{order.shipping_address.company}</p>
              )}
              <p>{order.shipping_address.address_1}</p>
              {order.shipping_address.address_2 && (
                <p>{order.shipping_address.address_2}</p>
              )}
              <p>
                {order.shipping_address.city}
                {order.shipping_address.province &&
                  `, ${order.shipping_address.province}`}{" "}
                {order.shipping_address.postal_code}
              </p>
              <p className="uppercase">{order.shipping_address.country_code}</p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  valueClassName
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-gray-600 dark:text-gray-400">{label}</dt>
      <dd
        className={cn(
          "font-medium text-gray-900 dark:text-white",
          valueClassName
        )}
      >
        {value}
      </dd>
    </div>
  );
}

const OrderLineItem = memo(function OrderLineItem({
  item,
  currencyCode
}: {
  item: HttpTypes.StoreOrderLineItem;
  currencyCode: string;
}) {
  return (
    <div className="flex gap-4 p-4">
      <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100 dark:bg-neutral-800">
        <ProductThumbnailImage
          src={item.thumbnail ?? null}
          alt=""
          className="h-full w-full object-cover"
          fallback={
            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
              No image
            </div>
          }
        />
      </div>
      {/* Below sm the price and Track button sit in a row under the title so
          the title gets the full column; from sm up they are a right-hand
          column beside it. */}
      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <TruncateTooltip
            as="h4"
            className="text-sm font-medium text-gray-900 dark:text-white"
          >
            {item.title}
          </TruncateTooltip>
          {item.subtitle && (
            <TruncateTooltip as="p" className="text-xs text-gray-500">
              {item.subtitle}
            </TruncateTooltip>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Qty: {item.quantity}
            {item.quantity > 1 && (
              <>
                {" "}
                &middot;{" "}
                {convertToLocale({
                  amount: item.unit_price,
                  currency_code: currencyCode
                })}{" "}
                each
              </>
            )}
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-row flex-wrap items-center justify-between gap-2 sm:flex-col sm:flex-nowrap sm:items-end sm:justify-start">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {convertToLocale({
              amount: item.total,
              currency_code: currencyCode
            })}
          </span>
          <TrackLineButton variantId={item.variant_id} title={item.title} />
        </div>
      </div>
    </div>
  );
});

// ---- Pagination ----

function Pagination({
  page,
  totalPages,
  onChange
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  return (
    <nav
      className="mt-6 flex items-center justify-between"
      aria-label="Order history pagination"
    >
      <Button
        variant="outline"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
      >
        Previous
      </Button>
      <span className="text-sm text-gray-500 dark:text-gray-400">
        Page {page} of {totalPages}
      </span>
      <Button
        variant="outline"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
      >
        Next
      </Button>
    </nav>
  );
}

// ---- Empty state ----

const OrdersEmptyState = memo(function OrdersEmptyState() {
  return (
    <div
      className="flex min-h-[250px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 text-center dark:border-gray-700"
      role="status"
    >
      <Package className="mb-4 h-12 w-12 text-gray-400" aria-hidden="true" />
      <p className="text-lg font-medium text-gray-900 dark:text-white">
        No orders yet
      </p>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Your orders will appear here once you place your first one.
      </p>
      <Button
        asChild
        className="mt-4 bg-purple-600 text-white hover:bg-purple-700"
      >
        <a href="/search">Browse products</a>
      </Button>
    </div>
  );
});

// ---- Loading skeletons ----

function OrdersLoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="border-border bg-card rounded-lg border">
          {/* Mirrors OrderRow: below sm a grid (title and price, meta line,
              full-width Reorder row); from sm up a row (icon, text, price). */}
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2 p-4 sm:flex sm:items-center sm:gap-4">
            <Skeleton className="hidden h-10 w-10 rounded-lg sm:block" />
            <div className="contents sm:block sm:flex-1 sm:space-y-2">
              <Skeleton className="col-start-1 row-start-1 h-4 w-40 max-w-full" />
              <Skeleton className="col-span-2 h-3 w-56 max-w-full" />
            </div>
            <Skeleton className="col-start-2 row-start-1 h-4 w-16" />
          </div>
          <div className="px-4 pb-4 sm:hidden">
            <Skeleton className="h-11 w-full rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ExpandedDetailSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="space-y-3 lg:col-span-2">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="border-border bg-card flex gap-4 rounded-lg border p-4"
          >
            <Skeleton className="h-14 w-14 flex-shrink-0 rounded-lg" />
            {/* Same wrapper as OrderLineItem: the price sits under the title
                below sm and beside it from sm up. */}
            <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:justify-between sm:gap-4">
              <div className="min-w-0 space-y-2">
                <Skeleton className="h-4 w-48 max-w-full" />
                <Skeleton className="h-3 w-24 max-w-full" />
              </div>
              <Skeleton className="h-4 w-16 flex-shrink-0" />
            </div>
          </div>
        ))}
      </div>
      <Skeleton className="h-32 w-full rounded-lg lg:col-span-1" />
    </div>
  );
}

// ---- Help sidebar ----

const HelpCard = memo(function HelpCard() {
  return (
    <Card className="overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-4 text-white">
        <h3 className="flex items-center gap-2 text-base font-semibold">
          <HelpCircle className="h-4 w-4" aria-hidden="true" />
          Need help with an order?
        </h3>
      </div>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Our support team is here to help with any order-related questions.
        </p>
        <div className="space-y-2">
          <Button
            variant="outline"
            className="w-full justify-start border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-purple-900 dark:bg-purple-900/20 dark:text-purple-300"
            asChild
          >
            <a href="/support">
              <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
              Chat support
            </a>
          </Button>
          <Button variant="outline" className="w-full justify-start" asChild>
            <a href="tel:">
              <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
              Call support
            </a>
          </Button>
          <Button variant="outline" className="w-full justify-start" asChild>
            <a href="mailto:">
              <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
              Email support
            </a>
          </Button>
        </div>
        <Separator />
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
            Quick links
          </h4>
          <ul className="space-y-1">
            {["Return policy", "Shipping information", "Track my order"].map(
              (label) => (
                <li key={label}>
                  <a
                    href="/support"
                    className="text-xs text-purple-600 hover:underline dark:text-purple-400"
                  >
                    {label}
                  </a>
                </li>
              )
            )}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
});

export default OrdersPage;
